import { useEffect, useMemo, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Select,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { ChecklistTemplateSummary, ChecklistTag, ChecklistStatus, Site } from '@cmms/types'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { usePermissions } from '../../hooks/usePermissions'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { SearchableSelect } from '../../components/SearchableSelect'
import { ChecklistBuilder } from './ChecklistBuilder'
import { ChecklistsImportDialog } from './ChecklistsImportDialog'
import { BulkActionBar } from '../../components/BulkActionBar'
import { useBulkSelection } from '../../hooks/useBulkSelection'

const STATUS_META: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Draft',     color: '#D97706' },
  published: { label: 'Published', color: '#059669' },
  archived:  { label: 'Archived',  color: '#94A3B8' },
}

export function ChecklistsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { checklistId: urlId } = useParams<{ checklistId?: string }>()
  const { can } = usePermissions()
  const { formatDate } = useOrgSettings()
  const confirmDlg = useConfirm()

  const [selectedId, setSelectedId] = useState<string | null>(urlId ?? null)
  useEffect(() => { if (urlId) setSelectedId(urlId) }, [urlId])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [tagFilter, setTagFilter] = useState<string>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)
  const [telemetryOpen, setTelemetryOpen] = useState(false)
  const [telemetryAssetId, setTelemetryAssetId] = useState('')

  const templatesQ = useQuery({
    queryKey: ['checklists', { status: statusFilter !== 'all' ? statusFilter : undefined, tag_id: tagFilter !== 'all' ? tagFilter : undefined, q: search.trim() || undefined }],
    queryFn: () => api.checklists.list({
      status: statusFilter !== 'all' ? statusFilter as ChecklistStatus : undefined,
      tag_id: tagFilter !== 'all' ? tagFilter : undefined,
      q: search.trim() || undefined,
    }),
  })
  const templates: ChecklistTemplateSummary[] = Array.isArray(templatesQ.data) ? templatesQ.data : []

  const tagsQ = useQuery({ queryKey: ['checklist-tags'], queryFn: () => api.checklists.listTags(), staleTime: 5 * 60_000 })
  const tags: ChecklistTag[] = Array.isArray(tagsQ.data) ? tagsQ.data : []
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const allSites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const bulk = useBulkSelection()
  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => api.checklists.bulkRemove(ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      if (okCount > 0 && skipCount === 0) toast.success(`Archived ${okCount} checklist${okCount === 1 ? '' : 's'}`)
      else if (okCount > 0 && skipCount > 0) toast.success(`Archived ${okCount}, skipped ${skipCount}`)
      else toast.error('Nothing was archived')
      qc.invalidateQueries({ queryKey: ['checklists'] })
      bulk.clear()
      if (selectedId && res.deleted.includes(selectedId)) setSelectedId(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk archive failed'),
  })
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const ok = await confirmDlg({
      title: `Archive ${ids.length} checklist${ids.length === 1 ? '' : 's'}?`,
      message: 'Archived checklists are hidden from this list. You can re-import or restore them anytime.',
      confirmLabel: 'Archive',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  if (!selectedId && templates.length > 0) {
    const first = templates[0]!.id
    setSelectedId(first)
    navigate(`/checklists/${first}`, { replace: true })
  }

  function handleSelect(id: string) {
    setSelectedId(id)
    navigate(`/checklists/${id}`, { replace: true })
  }

  const selected = selectedId ? templates.find((t) => t.id === selectedId) ?? null : null

  const publishM = useMutation({
    mutationFn: (id: string) => api.checklists.publish(id),
    onSuccess: () => { toast.success('Checklist published'); qc.invalidateQueries({ queryKey: ['checklists'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to publish'),
  })

  const duplicateM = useMutation({
    mutationFn: (id: string) => api.checklists.duplicate(id),
    onSuccess: (t) => { toast.success('Checklist duplicated'); qc.invalidateQueries({ queryKey: ['checklists'] }); handleSelect(t.id) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const assetsQ = useQuery({ queryKey: ['assets'], queryFn: () => api.assets.list({}), staleTime: 60_000, enabled: telemetryOpen })
  const assets = Array.isArray(assetsQ.data) ? assetsQ.data : []

  const telemetryM = useMutation({
    mutationFn: (assetId: string) => api.checklists.generateTelemetryChecklist(selected!.id, assetId),
    onSuccess: (t) => {
      toast.success('Telemetry questions generated')
      qc.invalidateQueries({ queryKey: ['checklists'] })
      qc.invalidateQueries({ queryKey: ['checklist', t.id] })
      setTelemetryOpen(false)
      setTelemetryAssetId('')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to generate telemetry questions'),
  })

  const restoreM = useMutation({
    mutationFn: (id: string) => api.checklists.restore(id),
    onSuccess: (_, id) => { toast.success('Checklist restored as draft'); qc.invalidateQueries({ queryKey: ['checklists'] }); qc.invalidateQueries({ queryKey: ['checklist', id] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to restore'),
  })

  const archiveM = useMutation({
    mutationFn: (id: string) => api.checklists.archive(id),
    onSuccess: () => { toast.success('Checklist archived'); qc.invalidateQueries({ queryKey: ['checklists'] }); setSelectedId(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <TextField placeholder="Search checklists" value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: { xs: '100%', md: 280 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
      {/* Status filter */}
      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small" variant="standard" disableUnderline
        renderValue={() => (
          <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: statusFilter !== 'all' ? STATUS_META[statusFilter]?.color : 'text.secondary' }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: statusFilter !== 'all' ? 'text.primary' : 'text.secondary' }}>
              {statusFilter === 'all' ? 'Status' : STATUS_META[statusFilter]?.label}
            </Typography>
          </Stack>
        )}
        sx={{ height: 32, borderRadius: 2, border: '1px solid', borderColor: statusFilter !== 'all' ? 'primary.main' : 'divider', bgcolor: 'background.paper',
          '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 } }}>
        <MenuItem value="all" sx={{ fontSize: 13 }}>All statuses</MenuItem>
        <MenuItem value="draft" sx={{ fontSize: 13 }}>Draft</MenuItem>
        <MenuItem value="published" sx={{ fontSize: 13 }}>Published</MenuItem>
        <MenuItem value="archived" sx={{ fontSize: 13 }}>Archived</MenuItem>
      </Select>
      {/* Tag filter */}
      {tags.length > 0 && (
        <Select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} size="small" variant="standard" disableUnderline
          renderValue={() => (
            <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}>
              <LocalOfferOutlinedIcon sx={{ fontSize: 13, color: tagFilter !== 'all' ? tags.find((t) => t.id === tagFilter)?.color : 'text.secondary' }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tagFilter !== 'all' ? 'text.primary' : 'text.secondary' }}>
                {tagFilter === 'all' ? 'Tag' : tags.find((t) => t.id === tagFilter)?.name ?? 'Tag'}
              </Typography>
            </Stack>
          )}
          sx={{ height: 32, borderRadius: 2, border: '1px solid', borderColor: tagFilter !== 'all' ? 'primary.main' : 'divider', bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 } }}>
          <MenuItem value="all" sx={{ fontSize: 13 }}>All tags</MenuItem>
          {tags.map((t) => (
            <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: t.color }} />
                <span>{t.name}</span>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      )}
      <Box sx={{ flex: 1 }} />
      {/* Manage tags — right corner */}
      {can('checklists:create') && (
        <Tooltip title="Create, edit, and delete tags" arrow>
          <Button size="small" startIcon={<LocalOfferOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => setTagsOpen(true)}
            sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', textTransform: 'none',
              border: '1px solid', borderColor: 'divider', borderRadius: 2, height: 32, px: 1.5 }}>
            Manage tags
          </Button>
        </Tooltip>
      )}
    </Stack>
  )

  return (
    <>
      <PageShell title="Checklists" toolbar={toolbar} scrollContent={false}
        actions={
          can('checklists:create') ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setImportOpen(true)}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, borderColor: 'divider', color: 'text.primary' }}>
                Import CSV
              </Button>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
                <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                  New checklist
                </Button>
              </motion.div>
            </Stack>
          ) : undefined
        }>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>
          {templatesQ.isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              {/* List */}
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0, borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                  {can('checklists:delete') && templates.length > 0 && (
                    <Checkbox size="small"
                      checked={bulk.allSelected(templates.map((t) => t.id))}
                      indeterminate={bulk.someSelected(templates.map((t) => t.id))}
                      onChange={(e) => bulk.replaceWith(e.target.checked ? templates.map((t) => t.id) : [])}
                      sx={{ p: 0.5 }} />
                  )}
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
                    {bulk.count > 0 ? `${bulk.count} selected` : `${templates.length} checklist${templates.length === 1 ? '' : 's'}`}
                  </Typography>
                </Stack>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1, scrollbarWidth: 'thin' }}>
                  {templates.map((t, i) => {
                    const sel = t.id === selectedId
                    const st = STATUS_META[t.status] ?? { label: 'Draft', color: '#D97706' }
                    return (
                      <motion.div key={t.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(0.4, i * 0.015), duration: 0.24 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => handleSelect(t.id)}
                          sx={{
                            position: 'relative', px: 1.5, py: 1.5, cursor: 'pointer',
                            transition: 'background-color 120ms',
                            bgcolor: sel ? primary.selected : 'transparent',
                            '&:hover': { bgcolor: sel ? primary.selectedHover : (th) => th.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                            '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                          }}>
                          {can('checklists:delete') && (
                            <Checkbox size="small" checked={bulk.isSelected(t.id)}
                              onClick={(e) => { e.stopPropagation(); bulk.toggle(t.id) }}
                              onChange={() => {}}
                              sx={{ p: 0.5 }} />
                          )}
                          <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: (th) => th.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.05)', color: 'text.secondary', flexShrink: 0 }}>
                            <ChecklistOutlinedIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500 }} noWrap>{t.name}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                              <Chip label={st.label} size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${st.color}18`, color: st.color, '& .MuiChip-label': { px: 0.5 } }} />
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{t.section_count}s · {t.item_count}q</Typography>
                              <CustomerScopeChip customerId={t.customer_id} />
                            </Stack>
                          </Box>
                        </Stack>
                      </motion.div>
                    )
                  })}
                  {templates.length === 0 && (
                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No checklists yet.</Typography>
                    </Box>
                  )}
                </Box>
                <BulkActionBar count={bulk.count} label="checklist"
                  deleting={bulkDeleteM.isPending}
                  onClear={bulk.clear} onDelete={handleBulkDelete} />
              </Box>

              {/* Detail / Builder */}
              {selected ? (
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {/* Header */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>{selected.name}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                        <Chip label={STATUS_META[selected.status]?.label} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700,
                            bgcolor: `${STATUS_META[selected.status]?.color}18`, color: STATUS_META[selected.status]?.color }} />
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>v{selected.version} · {selected.section_count} sections · {selected.item_count} items</Typography>
                        {selected.tags.map((tag) => (
                          <Chip key={tag.id} label={tag.name} size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: `${tag.color}18`, color: tag.color }} />
                        ))}
                        {(selected.sites?.length ?? 0) > 0 ? selected.sites.map((s) => (
                          <Chip key={s.site_id} icon={<LocationOnOutlinedIcon sx={{ fontSize: 12 }} />} label={s.site_name} size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />
                        )) : (
                          <Chip icon={<LocationOnOutlinedIcon sx={{ fontSize: 12 }} />} label="All sites" size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: (t) => `${t.palette.primary.main}12`, color: 'primary.main' }} />
                        )}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.75}>
                      {/* Edit metadata — available for drafts */}
                      {selected.status === 'draft' && can('checklists:create') && (
                        <Tooltip title="Edit name, description, tags, and sites" arrow>
                          <IconButton size="small" onClick={() => setEditOpen(true)}
                            sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Published — edit metadata + create new version to edit content */}
                      {selected.status === 'published' && can('checklists:create') && (
                        <>
                          <Tooltip title="Edit name, description, tags, and sites" arrow>
                            <IconButton size="small" onClick={() => setEditOpen(true)}
                              sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Create a new draft version to edit questions" arrow>
                            <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                              onClick={async () => {
                                try {
                                  const newVer = await api.checklists.newVersion(selected.id)
                                  qc.invalidateQueries({ queryKey: ['checklists'] })
                                  handleSelect(newVer.id)
                                  toast.success(`Draft v${newVer.version} created — you can now edit`)
                                } catch (e) { toast.error(e instanceof ApiError ? e.message : 'Failed') }
                              }}
                              sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
                              New version
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {selected.status === 'draft' && can('checklists:publish') && (
                        <Tooltip title="Publish this checklist" arrow>
                          <Button variant="contained" size="small" startIcon={<PublishOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => publishM.mutate(selected.id)} disabled={publishM.isPending}
                            sx={{ fontSize: 12, fontWeight: 600 }}>Publish</Button>
                        </Tooltip>
                      )}
                      {can('checklists:create') && (
                        <Tooltip title={selected.status === 'draft' ? 'Auto-generate one question per asset soft point' : 'Create a new version to change telemetry questions'} arrow>
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            <IconButton size="small"
                              onClick={() => { setTelemetryAssetId(selected.telemetry_asset_id ?? ''); setTelemetryOpen(true) }}
                              disabled={selected.status !== 'draft'}
                              sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                              <TimelineOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                      {can('checklists:create') && (
                        <Tooltip title="Duplicate" arrow>
                          <IconButton size="small" onClick={() => duplicateM.mutate(selected.id)}
                            disabled={duplicateM.isPending}
                            sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {selected.status === 'archived' ? (
                        can('checklists:create') && (
                          <Tooltip title="Restore this checklist as a draft" arrow>
                            <Button variant="outlined" size="small" startIcon={<RestoreOutlinedIcon sx={{ fontSize: 14 }} />}
                              onClick={() => restoreM.mutate(selected.id)}
                              disabled={restoreM.isPending}
                              sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
                              {restoreM.isPending ? 'Restoring…' : 'Restore'}
                            </Button>
                          </Tooltip>
                        )
                      ) : (
                        can('checklists:delete') && (
                          <Tooltip title="Archive" arrow>
                            <IconButton size="small" onClick={async () => {
                              const ok = await confirmDlg({ title: 'Archive this checklist?', message: 'It will be removed from the active list but existing responses will be preserved.', confirmLabel: 'Archive', variant: 'warning' })
                              if (ok) archiveM.mutate(selected.id)
                            }}
                              sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34, '&:hover': { color: 'error.main' } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Stack>
                  </Stack>

                  {/* Builder */}
                  <ChecklistBuilder templateId={selected.id} editable={selected.status === 'draft'} />
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ChecklistOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Select a checklist or create a new one</Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </PageShell>

      {createOpen && <NewChecklistDialog tags={tags} sites={allSites} onClose={() => setCreateOpen(false)} onCreated={(id) => { handleSelect(id); qc.invalidateQueries({ queryKey: ['checklists'] }) }} />}
      <ChecklistsImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      {tagsOpen && <TagsDialog tags={tags} canCreate={can('checklists:create')} canDelete={can('checklists:delete')}
        onClose={() => { setTagsOpen(false); qc.invalidateQueries({ queryKey: ['checklist-tags'] }) }} />}
      {editOpen && selected && <EditChecklistDialog template={selected} tags={tags} sites={allSites} onClose={() => setEditOpen(false)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['checklists'] }); qc.invalidateQueries({ queryKey: ['checklist', selected.id] }); setEditOpen(false) }} />}

      {telemetryOpen && selected && (
        <Dialog open onClose={() => setTelemetryOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
          <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Telemetry checklist</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              Pick an asset — one number question will be generated for each of its soft points.
              Re-running this replaces any previously generated telemetry questions on this checklist.
            </Typography>
            <SearchableSelect
              value={telemetryAssetId}
              onChange={setTelemetryAssetId}
              options={assets}
              getValue={(a) => a.id}
              getLabel={(a) => `${a.name}${a.asset_code ? ` (${a.asset_code})` : ''}`}
              placeholder="Select asset"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setTelemetryOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!telemetryAssetId || telemetryM.isPending}
              onClick={() => telemetryM.mutate(telemetryAssetId)}>
              {telemetryM.isPending ? 'Generating…' : 'Generate questions'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

function NewChecklistDialog({ tags: initialTags, sites, onClose, onCreated }: { tags: ChecklistTag[]; sites: Site[]; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [tagIds, setTagIds] = useState<string[]>([])
  const [siteIds, setSiteIds] = useState<string[]>([])
  const [allSitesMode, setAllSitesMode] = useState(true)
  const [newTagName, setNewTagName] = useState('')
  const [creatingTag, setCreatingTag] = useState(false)
  // Catalog scope: null = shared across every customer in the org.
  const [customerId, setCustomerId] = useState<string | null>(null)

  // Site picker search — chips can number in the hundreds under
  // large customers (e.g. DBS/JLL has 400+ sites), so a free-text
  // filter is the fastest way to land on the ones the admin means.
  const [siteSearch, setSiteSearch] = useState('')

  // Two-stage derivation so the search field can't accidentally
  // wipe out already-picked selections when the query hides them:
  //   scopedSites  = customer scope only (drives the drop-effect)
  //   visibleSites = scoped + search filter (drives the render)
  const scopedSites = useMemo(
    () => customerId ? sites.filter((s) => s.customer_id === customerId) : sites,
    [customerId, sites],
  )
  const visibleSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase()
    if (!q) return scopedSites
    return scopedSites.filter((s) => s.name.toLowerCase().includes(q))
  }, [scopedSites, siteSearch])
  // When the customer changes, drop any previously-selected sites
  // that no longer belong to the new customer. Search-hidden sites
  // stay selected (they'd come back the moment the search clears).
  useEffect(() => {
    if (!customerId) return
    const validIds = new Set(scopedSites.map((s) => s.id))
    setSiteIds((prev) => prev.filter((id) => validIds.has(id)))
  }, [customerId, scopedSites])

  // Re-fetch tags so newly created ones show up
  const tagsQ = useQuery({ queryKey: ['checklist-tags'], queryFn: () => api.checklists.listTags() })
  const tags: ChecklistTag[] = Array.isArray(tagsQ.data) ? tagsQ.data : initialTags

  const createTagM = useMutation({
    mutationFn: () => api.checklists.createTag({ name: newTagName.trim() }),
    onSuccess: (tag) => {
      setTagIds((prev) => [...prev, tag.id])
      setNewTagName('')
      setCreatingTag(false)
      qc.invalidateQueries({ queryKey: ['checklist-tags'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const createM = useMutation({
    mutationFn: () => api.checklists.create({
      name: name.trim(), description: description.trim() || undefined,
      tag_ids: tagIds.length ? tagIds : undefined,
      site_ids: allSitesMode ? undefined : siteIds.length ? siteIds : undefined,
      customer_id: customerId,
    }),
    onSuccess: (t) => { onCreated(t.id); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>New checklist</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus
            placeholder="e.g. HVAC Preventive Maintenance" helperText="Give it a clear, descriptive name" />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth multiline minRows={2}
            placeholder="What is this checklist used for?" />
          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="Leave as All to share with every customer. Pick one to scope this template to a single customer."
          />
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Tags</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {tags.map((tag) => {
                const sel = tagIds.includes(tag.id)
                return (
                  <Chip key={tag.id} label={tag.name} size="small" onClick={() => setTagIds((prev) => sel ? prev.filter((id) => id !== tag.id) : [...prev, tag.id])}
                    sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      bgcolor: sel ? `${tag.color}25` : 'action.hover', color: sel ? tag.color : 'text.secondary',
                      border: '1px solid', borderColor: sel ? tag.color : 'transparent' }} />
                )
              })}
            </Stack>
            {creatingTag ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField value={newTagName} onChange={(e) => setNewTagName(e.target.value)} size="small" fullWidth autoFocus
                  placeholder="Tag name..." onKeyDown={(e) => { if (e.key === 'Enter' && newTagName.trim()) createTagM.mutate() }}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }} />
                <Button size="small" onClick={() => createTagM.mutate()} disabled={!newTagName.trim() || createTagM.isPending}
                  sx={{ fontSize: 12, fontWeight: 600, minWidth: 50 }}>{createTagM.isPending ? '...' : 'Add'}</Button>
                <Button size="small" onClick={() => { setCreatingTag(false); setNewTagName('') }}
                  sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}>Cancel</Button>
              </Stack>
            ) : (
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setCreatingTag(true)}
                sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
                Create new tag
              </Button>
            )}
            </Box>

          {/* Site access */}
          {sites.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Site availability
                </Typography>
              </Stack>
              <FormControlLabel
                control={<Checkbox size="small" checked={allSitesMode} onChange={(e) => { setAllSitesMode(e.target.checked); if (e.target.checked) { setSiteIds([]); setSiteSearch('') } }} />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 500 }}>Available at all sites</Typography>}
                sx={{ mb: 1 }}
              />
              {!allSitesMode && scopedSites.length > 0 && (
                <TextField
                  size="small" fullWidth
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Search sites"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
                />
              )}
              {/* Select-all bar — labelled "matching" when a search
                  is active so admins realise it only bulk-picks the
                  visible filtered set, not every site under the
                  scope. Toggles based on whether every visible site
                  is already picked; middle state (partial) still
                  reads "Select all N" for clarity. */}
              {!allSitesMode && visibleSites.length > 0 && (() => {
                const visIds = visibleSites.map((s) => s.id)
                const pickedFromVisible = visIds.filter((id) => siteIds.includes(id)).length
                const allVisiblePicked = pickedFromVisible === visIds.length
                const label = siteSearch.trim()
                  ? `Select all ${visibleSites.length} matching`
                  : `Select all ${visibleSites.length}`
                return (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small"
                      onClick={() => setSiteIds((prev) => {
                        if (allVisiblePicked) {
                          // Toggle off — deselect just the visible set.
                          const drop = new Set(visIds)
                          return prev.filter((id) => !drop.has(id))
                        }
                        // Add every visible id, keeping any hidden-but-picked.
                        return Array.from(new Set([...prev, ...visIds]))
                      })}
                      sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: 'primary.main', minWidth: 0 }}>
                      {allVisiblePicked ? 'Deselect all' : label}
                    </Button>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                      {pickedFromVisible} / {visibleSites.length} picked
                    </Typography>
                  </Stack>
                )
              })()}
              {!allSitesMode && visibleSites.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {visibleSites.map((s) => {
                    const sel = siteIds.includes(s.id)
                    return (
                      <Chip key={s.id} label={s.name} size="small"
                        onClick={() => setSiteIds((prev) => sel ? prev.filter((id) => id !== s.id) : [...prev, s.id])}
                        sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          bgcolor: sel ? (t) => `${t.palette.primary.main}18` : 'action.hover',
                          color: sel ? 'primary.main' : 'text.secondary',
                          border: '1px solid', borderColor: sel ? 'primary.main' : 'transparent' }} />
                    )
                  })}
                </Stack>
              )}
              {!allSitesMode && visibleSites.length === 0 && scopedSites.length > 0 && siteSearch.trim() !== '' && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                  No sites match "{siteSearch}".
                </Typography>
              )}
              {!allSitesMode && scopedSites.length === 0 && customerId && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                  No sites under this customer yet.
                </Typography>
              )}
              {/* When a search filter hides sites the admin has
                  already picked, surface them under a "Selected"
                  strip so the counts + save action stay coherent. */}
              {!allSitesMode && siteSearch.trim() !== '' && (() => {
                const visibleIds = new Set(visibleSites.map((s) => s.id))
                const hidden = scopedSites.filter((s) => siteIds.includes(s.id) && !visibleIds.has(s.id))
                if (hidden.length === 0) return null
                return (
                  <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.75 }}>
                      Selected (hidden by search)
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {hidden.map((s) => (
                        <Chip key={s.id} label={s.name} size="small"
                          onClick={() => setSiteIds((prev) => prev.filter((id) => id !== s.id))}
                          sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main',
                            border: '1px solid', borderColor: 'primary.main' }} />
                      ))}
                    </Stack>
                  </Box>
                )
              })()}
              {!allSitesMode && scopedSites.length > 0 && siteIds.length === 0 && (
                <Typography sx={{ fontSize: 11.5, color: '#D97706', mt: 0.75 }}>Select at least one site, or enable "Available at all sites"</Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || createM.isPending} onClick={() => createM.mutate()}>
          {createM.isPending ? 'Creating...' : 'Create checklist'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function EditChecklistDialog({ template, tags, sites, onClose, onSaved }: {
  template: ChecklistTemplateSummary; tags: ChecklistTag[]; sites: Site[]; onClose: () => void; onSaved: () => void
}) {
  const [name, setName] = useState(template.name)
  const [description, setDescription] = useState(template.description ?? '')
  const [tagIds, setTagIds] = useState<string[]>(template.tags.map((t) => t.id))
  const [siteIds, setSiteIds] = useState<string[]>(template.sites?.map((s) => s.site_id) ?? [])
  const [allSitesMode, setAllSitesMode] = useState((template.sites?.length ?? 0) === 0)
  const [customerId, setCustomerId] = useState<string | null>(template.customer_id ?? null)

  // Search state for the site chip picker. See NewChecklistDialog
  // for the two-stage derivation rationale — same shape here.
  const [siteSearch, setSiteSearch] = useState('')
  const scopedSites = useMemo(
    () => customerId ? sites.filter((s) => s.customer_id === customerId) : sites,
    [customerId, sites],
  )
  const visibleSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase()
    if (!q) return scopedSites
    return scopedSites.filter((s) => s.name.toLowerCase().includes(q))
  }, [scopedSites, siteSearch])
  // When the customer changes, drop selected sites that no longer
  // belong to the new customer. Search-hidden picks stay selected.
  useEffect(() => {
    if (!customerId) return
    const validIds = new Set(scopedSites.map((s) => s.id))
    setSiteIds((prev) => prev.filter((id) => validIds.has(id)))
  }, [customerId, scopedSites])

  const updateM = useMutation({
    // Always send `description` (even ''), not `undefined` — omitting it
    // means "leave unchanged" to the backend's sparse update, so clearing
    // the field would otherwise silently survive a "successful" save.
    mutationFn: () => api.checklists.update(template.id, {
      name: name.trim(), description: description.trim(),
      tag_ids: tagIds, site_ids: allSitesMode ? [] : siteIds,
      customer_id: customerId,
    }),
    onSuccess: () => { toast.success('Checklist updated'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit checklist</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth multiline minRows={2} />
          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="All = shared across every customer. Pick one to scope this template."
          />
          {tags.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Tags</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => {
                  const sel = tagIds.includes(tag.id)
                  return (
                    <Chip key={tag.id} label={tag.name} size="small" onClick={() => setTagIds((prev) => sel ? prev.filter((id) => id !== tag.id) : [...prev, tag.id])}
                      sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        bgcolor: sel ? `${tag.color}25` : 'action.hover', color: sel ? tag.color : 'text.secondary',
                        border: '1px solid', borderColor: sel ? tag.color : 'transparent' }} />
                  )
                })}
              </Stack>
            </Box>
          )}
          {sites.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Site availability</Typography>
              </Stack>
              <FormControlLabel
                control={<Checkbox size="small" checked={allSitesMode} onChange={(e) => { setAllSitesMode(e.target.checked); if (e.target.checked) { setSiteIds([]); setSiteSearch('') } }} />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 500 }}>Available at all sites</Typography>}
                sx={{ mb: 1 }}
              />
              {!allSitesMode && scopedSites.length > 0 && (
                <TextField
                  size="small" fullWidth
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Search sites"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
                />
              )}
              {/* Select-all bar — mirrors NewChecklistDialog. */}
              {!allSitesMode && visibleSites.length > 0 && (() => {
                const visIds = visibleSites.map((s) => s.id)
                const pickedFromVisible = visIds.filter((id) => siteIds.includes(id)).length
                const allVisiblePicked = pickedFromVisible === visIds.length
                const label = siteSearch.trim()
                  ? `Select all ${visibleSites.length} matching`
                  : `Select all ${visibleSites.length}`
                return (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small"
                      onClick={() => setSiteIds((prev) => {
                        if (allVisiblePicked) {
                          const drop = new Set(visIds)
                          return prev.filter((id) => !drop.has(id))
                        }
                        return Array.from(new Set([...prev, ...visIds]))
                      })}
                      sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: 'primary.main', minWidth: 0 }}>
                      {allVisiblePicked ? 'Deselect all' : label}
                    </Button>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                      {pickedFromVisible} / {visibleSites.length} picked
                    </Typography>
                  </Stack>
                )
              })()}
              {!allSitesMode && visibleSites.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {visibleSites.map((s) => {
                    const sel = siteIds.includes(s.id)
                    return (
                      <Chip key={s.id} label={s.name} size="small"
                        onClick={() => setSiteIds((prev) => sel ? prev.filter((id) => id !== s.id) : [...prev, s.id])}
                        sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          bgcolor: sel ? (t) => `${t.palette.primary.main}18` : 'action.hover',
                          color: sel ? 'primary.main' : 'text.secondary',
                          border: '1px solid', borderColor: sel ? 'primary.main' : 'transparent' }} />
                    )
                  })}
                </Stack>
              )}
              {!allSitesMode && visibleSites.length === 0 && scopedSites.length > 0 && siteSearch.trim() !== '' && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                  No sites match "{siteSearch}".
                </Typography>
              )}
              {!allSitesMode && scopedSites.length === 0 && customerId && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                  No sites under this customer yet.
                </Typography>
              )}
              {!allSitesMode && siteSearch.trim() !== '' && (() => {
                const visibleIds = new Set(visibleSites.map((s) => s.id))
                const hidden = scopedSites.filter((s) => siteIds.includes(s.id) && !visibleIds.has(s.id))
                if (hidden.length === 0) return null
                return (
                  <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.75 }}>
                      Selected (hidden by search)
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {hidden.map((s) => (
                        <Chip key={s.id} label={s.name} size="small"
                          onClick={() => setSiteIds((prev) => prev.filter((id) => id !== s.id))}
                          sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main',
                            border: '1px solid', borderColor: 'primary.main' }} />
                      ))}
                    </Stack>
                  </Box>
                )
              })()}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || updateM.isPending} onClick={() => updateM.mutate()}>
          {updateM.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TAG_COLORS = ['#0D9488', '#D97706', '#2563EB', '#DC2626', '#7C3AED', '#EA580C', '#059669', '#DB2777', '#0E7490', '#65A30D', '#475569', '#0284C7']

function TagsDialog({ tags, canCreate, canDelete, onClose }: { tags: ChecklistTag[]; canCreate: boolean; canDelete: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(TAG_COLORS[0]!)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  const createM = useMutation({
    mutationFn: () => api.checklists.createTag({ name: newName.trim(), color: newColor }),
    onSuccess: () => { setNewName(''); qc.invalidateQueries({ queryKey: ['checklist-tags'] }); toast.success('Tag created') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const updateM = useMutation({
    mutationFn: () => api.checklists.updateTag(editingId!, { name: editName.trim(), color: editColor }),
    onSuccess: () => { setEditingId(null); qc.invalidateQueries({ queryKey: ['checklist-tags'] }); toast.success('Tag updated') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const deleteM = useMutation({
    mutationFn: (id: string) => api.checklists.deleteTag(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-tags'] }); toast.success('Tag deleted') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Manage tags</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2.5 }}>
          Tags help organize your checklists by category. You can assign multiple tags to each checklist.
        </Typography>

        {/* Existing tags */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          {tags.map((tag) => (
            editingId === tag.id ? (
              <Stack key={tag.id} direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 1.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 99, bgcolor: editColor, flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => setEditColor(TAG_COLORS[(TAG_COLORS.indexOf(editColor) + 1) % TAG_COLORS.length]!)} />
                <TextField value={editName} onChange={(e) => setEditName(e.target.value)} size="small" fullWidth variant="standard"
                  InputProps={{ disableUnderline: true, sx: { fontSize: 13.5, fontWeight: 600 } }} autoFocus />
                <Button size="small" onClick={() => updateM.mutate()} disabled={!editName.trim() || updateM.isPending}
                  sx={{ fontSize: 12, fontWeight: 600, minWidth: 0 }}>Save</Button>
                <Button size="small" onClick={() => setEditingId(null)} sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}>Cancel</Button>
              </Stack>
            ) : (
              <Stack key={tag.id} direction="row" alignItems="center" spacing={1.5}
                sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                  transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                <Box sx={{ width: 16, height: 16, borderRadius: 99, bgcolor: tag.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{tag.name}</Typography>
                {canCreate && (
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" onClick={() => { setEditingId(tag.id); setEditName(tag.name); setEditColor(tag.color) }}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}>
                      <EditOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete" arrow>
                    <IconButton size="small" onClick={() => deleteM.mutate(tag.id)}
                      disabled={deleteM.isPending}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )
          ))}
          {tags.length === 0 && (
            <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
              <LocalOfferOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.75 }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No tags yet</Typography>
            </Box>
          )}
        </Stack>

        {/* Create new tag */}
        {canCreate && (<>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
          Create a tag
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title="Click to change color" arrow>
            <Box onClick={() => setNewColor(TAG_COLORS[(TAG_COLORS.indexOf(newColor) + 1) % TAG_COLORS.length]!)}
              sx={{ width: 28, height: 28, borderRadius: 99, bgcolor: newColor, flexShrink: 0, cursor: 'pointer',
                border: '2px solid', borderColor: 'divider', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.15)' } }} />
          </Tooltip>
          <TextField value={newName} onChange={(e) => setNewName(e.target.value)} size="small" fullWidth
            placeholder="e.g. Safety, HVAC, Electrical..."
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createM.mutate() }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
          <Button variant="contained" size="small" onClick={() => createM.mutate()}
            disabled={!newName.trim() || createM.isPending}
            sx={{ fontSize: 12, fontWeight: 600, minWidth: 70 }}>
            {createM.isPending ? '...' : 'Add'}
          </Button>
        </Stack>

        {/* Color palette */}
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {TAG_COLORS.map((c) => (
            <Box key={c} onClick={() => setNewColor(c)}
              sx={{ width: 20, height: 20, borderRadius: 99, bgcolor: c, cursor: 'pointer',
                border: '2px solid', borderColor: newColor === c ? 'text.primary' : 'transparent',
                transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.2)' } }} />
          ))}
        </Stack>
        </>)}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
